import React from 'react';
import { Gift, CreditCard, Wallet, Tag, X, Sparkles, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { SENIOR_IDS, calcPersonPrice as calcSharedPrice } from '../../lib/pricing';

const THERAPISTS = [
  { id: 'no_preference', name: 'Any Available' },
  { id: 'michael', name: 'Dr. Michael' },
  { id: 'ana', name: 'Ana' },
  { id: 'buggie', name: 'Buggie' },
  { id: 'leslie', name: 'Leslie' },
  { id: 'victor', name: 'Victor' },
  { id: 'eegi', name: 'Eegi' },
  { id: 'lana', name: 'Lana' },
  { id: 'mayara', name: 'Mayara' },
  { id: 'rino', name: 'Rino' },
  { id: 'salome', name: 'Salome' },
];

const BookingStepCheckout = ({
  formData, setFormData, loyaltyInfo, paymentConfig,
  promoApplied, promoCode, setPromoCode, promoError, checkingPromo, applyPromo, removePromo,
  thursdayDeal, lunchtimeDeal, firstVisitDeal,
  voucherApplied, voucherCode, setVoucherCode, voucherError, checkingVoucher, applyVoucher, removeVoucher,
  handleSubmit, loading, processingPayment,
}) => {
  // Location-based + Senior therapist price adjustment
  const isCouples = formData.service?.name?.toLowerCase().includes('couple');
  const isSenior1 = SENIOR_IDS.includes(formData.therapist_preference);
  const isSenior2 = SENIOR_IDS.includes(formData.therapist_preference_2);
  const isClarehall = formData.location === 'clarehall';
  const basePrice = formData.service?.price || 0;

  // Clarehall fixed pricing: €80/30min, €100/45min, €130/60min, €160/90min, €240/120min
  // City Centre senior rates: €100/45min, €120/60min, €150/90min, €240/120min
  const calcPersonPrice = (therapistId) => calcSharedPrice(formData.service, formData.location, therapistId);

  const effectivePricePerson1 = calcPersonPrice(formData.therapist_preference);
  const effectivePricePerson2 = isCouples ? calcPersonPrice(formData.therapist_preference_2) : 0;

  // Total price: for couples = person1 + person2, for singles = person1
  const effectivePrice = isCouples ? effectivePricePerson1 + effectivePricePerson2 : effectivePricePerson1;
  const priceDiff = effectivePrice - (isCouples ? basePrice * 2 : basePrice);
  const priceLabel = isClarehall ? 'Clarehall rate' : 'Senior Therapist rate';

  const loyaltyPercent = loyaltyInfo?.current_discount_percent || 0;
  const discountAmount = loyaltyPercent ? (effectivePrice * loyaltyPercent / 100) : 0;
  const onlineDiscountPercent = paymentConfig.online_booking_discount_percent || 5;
  const fullDiscountPercent = paymentConfig.full_payment_discount_percent || 15;
  const isDeposit = formData.payment_type === 'deposit';

  // STACKING RULES: Only ONE offer at a time
  let activeOffer = null;
  let activeOfferAmount = 0;
  let activeOfferLabel = '';

  if (promoApplied) {
    activeOffer = 'promo';
    activeOfferAmount = promoApplied.type === 'percentage'
      ? effectivePrice * promoApplied.value / 100
      : Math.min(promoApplied.value, effectivePrice);
    activeOfferLabel = promoApplied.description;
  } else if (lunchtimeDeal) {
    activeOffer = 'lunchtime';
    activeOfferAmount = effectivePrice * lunchtimeDeal.discount_percent / 100;
    activeOfferLabel = 'Lunchtime Deal (10%)';
  } else if (thursdayDeal) {
    activeOffer = 'thursday';
    activeOfferAmount = effectivePrice * thursdayDeal.discount_percent / 100;
    activeOfferLabel = 'Thursday Deal (5%)';
  } else if (firstVisitDeal) {
    activeOffer = 'firstVisit';
    activeOfferAmount = effectivePrice * firstVisitDeal.discount_percent / 100;
    activeOfferLabel = 'First Visit (5%)';
  } else if (loyaltyPercent > 0) {
    activeOffer = 'loyalty';
    activeOfferAmount = discountAmount;
    activeOfferLabel = `Loyalty (${loyaltyPercent}%)`;
  }

  const payDiscStacks = activeOffer === 'firstVisit' || !activeOffer || (activeOffer === 'promo' && promoApplied?.stacks_with_deposit);
  const computeTotals = (deposit) => {
    const pct = deposit ? onlineDiscountPercent : fullDiscountPercent;
    let offer = activeOfferAmount;
    let pay = effectivePrice * pct / 100;
    if (!payDiscStacks) {
      if (pay > offer) offer = 0; else pay = 0;
    }
    return { offer, pay, total: Math.max(0, effectivePrice - offer - pay) };
  };
  const totals = computeTotals(isDeposit);
  const fullPayPreview = computeTotals(false);
  const depositPreview = computeTotals(true);
  if (activeOfferAmount > 0 && totals.offer === 0) {
    activeOffer = null;
    activeOfferLabel = '';
  }
  activeOfferAmount = totals.offer;
  const onlineDiscountAmount = totals.pay;
  const payDiscountPercent = isDeposit ? onlineDiscountPercent : fullDiscountPercent;
  const depositStacks = payDiscStacks;
  const finalPrice = totals.total;
  const depositAmount = paymentConfig.deposit_amount;
  const payingNow = isDeposit ? depositAmount : finalPrice;
  const totalSavings = activeOfferAmount + onlineDiscountAmount;

  // Best Deal calculator
  const price = effectivePrice;
  const possibleDeals = [];
  if (firstVisitDeal && !lunchtimeDeal && !thursdayDeal) {
    possibleDeals.push({ label: 'First Visit + Deposit', amount: price * 0.10, requires: 'deposit', key: 'fv_deposit' });
    possibleDeals.push({ label: 'First Visit only', amount: price * 0.05, requires: null, key: 'fv' });
  }
  if (lunchtimeDeal) {
    possibleDeals.push({ label: 'Lunchtime Deal', amount: price * 0.10, requires: null, key: 'lunchtime' });
  }
  if (thursdayDeal) {
    possibleDeals.push({ label: 'Thursday Deal', amount: price * 0.05, requires: null, key: 'thursday' });
  }
  if (loyaltyPercent > 0) {
    possibleDeals.push({ label: `Loyalty (${loyaltyPercent}%)`, amount: price * loyaltyPercent / 100, requires: null, key: 'loyalty' });
  }
  possibleDeals.push({ label: 'Deposit only', amount: price * 0.05, requires: 'deposit', key: 'deposit_only' });
  possibleDeals.push({ label: `Pay in Full (${fullDiscountPercent}% off)`, amount: price * fullDiscountPercent / 100, requires: 'full', key: 'full_only' });
  if (firstVisitDeal && !lunchtimeDeal && !thursdayDeal) {
    possibleDeals.push({ label: 'First Visit + Pay in Full', amount: price * (0.05 + fullDiscountPercent / 100), requires: 'full', key: 'fv_full' });
  }

  const bestDeal = possibleDeals.reduce((best, d) => d.amount > best.amount ? d : best, possibleDeals[0]);
  const showBestDealTip = bestDeal && bestDeal.amount > totalSavings + 0.01;

  return (
    <div className="space-y-4">
      <h3 className="text-base sm:text-lg font-semibold text-stone-800">Review & Pay</h3>
      
      {totalSavings > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-sage-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3" data-testid="savings-summary-banner">
          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-emerald-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">You&apos;re saving €{totalSavings.toFixed(2)}!</p>
            <p className="text-xs text-emerald-600">
              {[activeOfferLabel, onlineDiscountAmount > 0 && `${isDeposit ? 'Deposit' : 'Pay in Full'} ${payDiscountPercent}%`].filter(Boolean).join(' + ')}
            </p>
          </div>
        </div>
      )}

      {showBestDealTip && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3" data-testid="best-deal-tip">
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Tag size={14} className="text-amber-600" />
          </div>
          <div>
            <p className="text-xs font-semibold text-amber-800">Tip: {bestDeal.label} saves you €{bestDeal.amount.toFixed(2)}!</p>
            <p className="text-[11px] text-amber-600">
              {bestDeal.requires === 'deposit' && !isDeposit && !promoApplied && 'Switch to deposit payment to unlock this deal.'}
              {bestDeal.requires === 'full' && isDeposit && 'Switch to Pay Full to unlock this deal.'}
              {bestDeal.requires === 'promo' && !promoApplied && 'Enter the promo code at checkout.'}
              {bestDeal.key === 'fv_deposit' && promoApplied && 'Remove your promo code and pay by deposit for the best savings.'}
              {bestDeal.key === 'fv_deposit' && !promoApplied && !isDeposit && 'Switch to deposit payment to unlock this deal.'}
              {bestDeal.key === 'lunchtime' && promoApplied && 'Remove your promo code to use the Lunchtime Deal instead.'}
              {bestDeal.key === 'loyalty' && promoApplied && 'Remove your promo code to use your loyalty discount instead.'}
            </p>
          </div>
        </div>
      )}
      
      {/* Booking Summary */}
      <div className="bg-stone-50 rounded-xl p-3 sm:p-4 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-stone-500">Service</span>
          <span className="font-medium text-right max-w-[60%]">{formData.service?.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-500">Duration</span>
          <span className="font-medium">{formData.service?.duration}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-500">Location</span>
          <span className="font-medium">{formData.location === 'city_centre' ? 'City Centre' : 'Clarehall'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-500">Date & Time</span>
          <span className="font-medium">{formData.date} @ {formData.time}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-stone-500">{isCouples ? 'Therapist 1' : 'Therapist'}</span>
          <span className="font-medium">{THERAPISTS.find(t => t.id === formData.therapist_preference)?.name || 'Any Available'}</span>
        </div>
        {isCouples && (
          <div className="flex justify-between">
            <span className="text-stone-500">Therapist 2</span>
            <span className="font-medium">{THERAPISTS.find(t => t.id === formData.therapist_preference_2)?.name || 'Any Available'}</span>
          </div>
        )}
        
        {/* Price Breakdown */}
        <div className="border-t pt-3 mt-3">
          <div className="flex justify-between">
            <span className="text-stone-500">Service Price</span>
            <span className="font-medium">€{effectivePrice}{isCouples ? ` (€${effectivePricePerson1} + €${effectivePricePerson2})` : ''}</span>
          </div>
          {priceDiff > 0 && (
            <div className="flex justify-between text-stone-500 text-xs" data-testid="senior-surcharge-line">
              <span>Includes {priceLabel}</span>
              <span className="text-stone-600">+€{priceDiff} upgrade</span>
            </div>
          )}
          {isCouples && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-xs text-rose-700 mt-1" data-testid="couples-booking-note">
              <p className="font-medium">Couples Booking — one booking for 2 people</p>
              <p className="text-rose-500">Total for both: €{effectivePrice}. Your partner does NOT need to book separately.</p>
            </div>
          )}
          {onlineDiscountAmount > 0 && (
            <div className="flex justify-between text-emerald-600 text-xs" data-testid={isDeposit ? 'online-discount-line' : 'full-payment-discount-line'}>
              <span>{isDeposit ? `Deposit Discount (${onlineDiscountPercent}%)` : `Pay in Full Discount (${fullDiscountPercent}%)`}</span>
              <span>-€{onlineDiscountAmount.toFixed(2)}</span>
            </div>
          )}
          {activeOffer === 'promo' && (
            <div className="flex justify-between text-rose-600 text-xs" data-testid="promo-discount-line">
              <span>{promoApplied?.description}</span>
              <span>-€{activeOfferAmount.toFixed(2)}</span>
            </div>
          )}
          {activeOffer === 'thursday' && (
            <div className="flex justify-between text-purple-600 text-xs" data-testid="thursday-discount-line">
              <span>Thursday Deal (5%)</span>
              <span>-€{activeOfferAmount.toFixed(2)}</span>
            </div>
          )}
          {activeOffer === 'lunchtime' && (
            <div className="flex justify-between text-amber-600 text-xs" data-testid="lunchtime-discount-line">
              <span>Lunchtime Deal (10%)</span>
              <span>-€{activeOfferAmount.toFixed(2)}</span>
            </div>
          )}
          {activeOffer === 'firstVisit' && (
            <div className="flex justify-between text-sky-600 text-xs" data-testid="first-visit-discount-line">
              <span>First Visit Discount (5%)</span>
              <span>-€{activeOfferAmount.toFixed(2)}</span>
            </div>
          )}
          {activeOffer === 'loyalty' && (
            <div className="flex justify-between text-amber-600 text-xs" data-testid="loyalty-discount-line">
              <span>Loyalty Discount ({loyaltyPercent}%)</span>
              <span>-€{activeOfferAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold mt-1">
            <span>Total</span>
            <span className="text-luxury-green">€{finalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Payment Type Selection */}
      <div className="space-y-2">
        <p className="text-xs sm:text-sm font-medium text-stone-600">Payment Option</p>
        
        {voucherApplied ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gift size={18} className="text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Voucher Applied!</p>
                <p className="text-xs text-green-600">
                  {voucherApplied.code} • Balance: €{voucherApplied.remaining_balance}
                </p>
              </div>
            </div>
            <button onClick={removeVoucher} className="text-green-600 hover:text-green-800">
              <X size={18} />
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setFormData({ ...formData, payment_type: 'full' })}
                className={`p-3 rounded-xl border-2 text-left transition-all relative ${
                  formData.payment_type === 'full' ? 'border-luxury-green bg-sage-50' : 'border-stone-200 hover:border-sage-300'
                }`}
                data-testid="payment-full-btn"
              >
                <div className="absolute -top-2.5 right-2 bg-luxury-green text-luxury-gold text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="full-discount-badge">
                  SAVE {fullDiscountPercent}%
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard size={16} className="text-luxury-green" />
                  <span className="text-xs sm:text-sm font-medium">Pay Full</span>
                </div>
                <p className="text-base sm:text-lg font-bold text-luxury-green">€{fullPayPreview.total.toFixed(2)}</p>
                <p className="text-[10px] text-emerald-600 font-medium">
                  Save €{(effectivePrice - fullPayPreview.total).toFixed(2)} on total
                </p>
              </button>
              <button
                onClick={() => setFormData({ ...formData, payment_type: 'deposit' })}
                className={`p-3 rounded-xl border-2 text-left transition-all relative ${
                  formData.payment_type === 'deposit' ? 'border-luxury-green bg-sage-50' : 'border-stone-200 hover:border-sage-300'
                }`}
                data-testid="payment-deposit-btn"
              >
                <div className="absolute -top-2.5 right-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid="deposit-discount-badge">
                  SAVE {onlineDiscountPercent}%
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Wallet size={16} className="text-luxury-green" />
                  <span className="text-xs sm:text-sm font-medium">Deposit</span>
                </div>
                <p className="text-base sm:text-lg font-bold text-luxury-green">€{depositAmount}</p>
                <p className="text-[10px] text-emerald-600 font-medium">
                  Save €{(effectivePrice - depositPreview.total).toFixed(2)} on total — rest at studio
                </p>
              </button>
            </div>

            {isDeposit && depositPreview.total - fullPayPreview.total > 0.009 && (
              <button
                onClick={() => setFormData({ ...formData, payment_type: 'full' })}
                className="mt-2 w-full flex items-center justify-between gap-2 bg-luxury-green/5 border border-luxury-green/25 rounded-xl px-3 py-2.5 text-left hover:bg-luxury-green/10 transition-colors"
                data-testid="pay-full-nudge"
              >
                <span className="text-xs text-luxury-green">
                  <strong>Save €{(depositPreview.total - fullPayPreview.total).toFixed(2)} more</strong> by paying in full today ({fullDiscountPercent}% off)
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wide bg-luxury-green text-luxury-gold px-2.5 py-1 rounded-full whitespace-nowrap">
                  Switch &amp; Save
                </span>
              </button>
            )}
            
            {/* Promo Code */}
            <div className="mt-3">
              {promoApplied ? (
                <>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag size={16} className="text-rose-600" />
                    <div>
                      <p className="text-sm font-medium text-rose-800">{promoApplied.description}</p>
                      <p className="text-xs text-rose-600">
                        {promoApplied.code} • -{promoApplied.type === 'percentage' ? `${promoApplied.value}%` : `€${promoApplied.value}`}
                      </p>
                    </div>
                  </div>
                  <button onClick={removePromo} className="text-rose-600 hover:text-rose-800">
                    <X size={16} />
                  </button>
                </div>
                {promoApplied.code === 'TREATHER' && (
                  <div className="mt-2 bg-rose-50 border border-rose-200 rounded-xl p-3 space-y-2" data-testid="treather-recipient-section">
                    <p className="text-xs font-medium text-rose-700">
                      This code is for treating a special lady — who is it for? <span className="text-rose-400">*</span>
                    </p>
                    <Input
                      placeholder="Her name (e.g. Mary)"
                      value={formData.treat_recipient_name || ''}
                      onChange={(e) => setFormData({ ...formData, treat_recipient_name: e.target.value })}
                      className="text-sm h-9 bg-white"
                      data-testid="treat-recipient-input"
                    />
                    <Input
                      placeholder="Add a personal message for her (optional)"
                      value={formData.treat_message || ''}
                      onChange={(e) => setFormData({ ...formData, treat_message: e.target.value })}
                      className="text-sm h-9 bg-white"
                      data-testid="treat-message-input"
                    />
                    {!(formData.treat_recipient_name || '').trim() && (
                      <p className="text-xs text-rose-500" data-testid="treat-recipient-required-msg">
                        Please tell us who you're treating to use this code.
                      </p>
                    )}
                  </div>
                )}
                </>
              ) : (
                <>
                  <p className="text-xs text-stone-500 mb-2">Have a promo code?</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                      <Input
                        placeholder="e.g. TREATHER"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        className="pl-9 text-sm h-9"
                        data-testid="promo-code-input"
                      />
                    </div>
                    <Button
                      onClick={applyPromo}
                      disabled={!promoCode.trim() || checkingPromo}
                      variant="outline"
                      className="h-9 px-4 text-sm"
                      data-testid="apply-promo-btn"
                    >
                      {checkingPromo ? '...' : 'Apply'}
                    </Button>
                  </div>
                  {promoError && <p className="text-xs text-red-500 mt-1" data-testid="promo-error">{promoError}</p>}
                </>
              )}
            </div>

            {/* Deal Status Banners */}
            {thursdayDeal && activeOffer === 'thursday' && (
              <div className="mt-2 bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center gap-2" data-testid="thursday-deal-applied">
                <Sparkles size={16} className="text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-purple-800">Thursday Deal Applied!</p>
                  <p className="text-xs text-purple-600">Extra 5% off with your selected therapist</p>
                </div>
              </div>
            )}
            {thursdayDeal && activeOffer !== 'thursday' && (
              <div className="mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center gap-2 opacity-60">
                <Sparkles size={16} className="text-stone-400" />
                <div>
                  <p className="text-sm text-stone-500">Thursday Deal available but another offer is active</p>
                  <p className="text-xs text-stone-400">Only one offer can be used at a time</p>
                </div>
              </div>
            )}
            {lunchtimeDeal && activeOffer === 'lunchtime' && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2" data-testid="lunchtime-deal-applied">
                <Sun size={16} className="text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Lunchtime Deal Applied!</p>
                  <p className="text-xs text-amber-600">10% off any treatment (Mon–Fri 12pm–3pm)</p>
                </div>
              </div>
            )}
            {lunchtimeDeal && activeOffer !== 'lunchtime' && activeOffer !== null && (
              <div className="mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center gap-2 opacity-60">
                <Sun size={16} className="text-stone-400" />
                <div>
                  <p className="text-sm text-stone-500">Lunchtime Deal available but another offer is active</p>
                  <p className="text-xs text-stone-400">Only one offer can be used at a time</p>
                </div>
              </div>
            )}
            {firstVisitDeal && activeOffer === 'firstVisit' && (
              <div className="mt-2 bg-sky-50 border border-sky-200 rounded-xl p-3 flex items-center gap-2" data-testid="first-visit-applied">
                <Gift size={16} className="text-sky-600" />
                <div>
                  <p className="text-sm font-medium text-sky-800">Welcome! First Visit 5% Off</p>
                  <p className="text-xs text-sky-600">Stacks with the 5% deposit discount</p>
                </div>
              </div>
            )}
            {firstVisitDeal && activeOffer !== 'firstVisit' && activeOffer !== null && (
              <div className="mt-2 bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center gap-2 opacity-60">
                <Gift size={16} className="text-stone-400" />
                <div>
                  <p className="text-sm text-stone-500">First Visit discount available but another offer is active</p>
                  <p className="text-xs text-stone-400">
                    {promoApplied ? 'Remove your promo code to use First Visit + Deposit instead' : 'A better automatic deal is applied for this time slot'}
                  </p>
                </div>
              </div>
            )}

            {/* Voucher Input */}
            <div className="mt-3">
              <p className="text-xs text-stone-500 mb-2">Have a gift voucher?</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    placeholder="Enter voucher code"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                    className="pl-9 text-sm h-9"
                  />
                </div>
                <Button
                  onClick={applyVoucher}
                  disabled={!voucherCode.trim() || checkingVoucher}
                  variant="outline"
                  className="h-9 px-4 text-sm"
                >
                  {checkingVoucher ? '...' : 'Apply'}
                </Button>
              </div>
              {voucherError && <p className="text-xs text-red-500 mt-1">{voucherError}</p>}
            </div>
          </>
        )}
      </div>

      {/* Voucher Price Summary */}
      {voucherApplied && (
        <div className="bg-green-50 rounded-xl p-3 text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-stone-600">Total</span>
            <span>€{finalPrice.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-green-600">
            <span>Voucher ({voucherApplied.code})</span>
            <span>-€{Math.min(voucherApplied.remaining_balance, finalPrice).toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-semibold text-base mt-2 pt-2 border-t border-green-200">
            <span>To Pay</span>
            <span className="text-luxury-green">€{Math.max(0, finalPrice - voucherApplied.remaining_balance).toFixed(2)}</span>
          </div>
          {voucherApplied.remaining_balance >= finalPrice && (
            <p className="text-xs text-green-600 mt-1">✓ Voucher covers full amount!</p>
          )}
        </div>
      )}

      {/* Deposit Summary */}
      {isDeposit && !voucherApplied && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm" data-testid="deposit-summary">
          <p className="font-medium text-emerald-800 mb-2">Deposit Payment Summary</p>
          <div className="space-y-1">
            <div className="flex justify-between text-emerald-700 text-xs">
              <span>Deposit (pay now)</span>
              <span className="font-medium">€{depositAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-emerald-700 text-xs">
              <span>Remaining at visit</span>
              <span className="font-medium">€{(finalPrice - depositAmount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-emerald-800 font-semibold text-xs pt-1 border-t border-emerald-200">
              <span>You save</span>
              <span>€{onlineDiscountAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Policy */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3 text-xs">
        <p className="font-medium text-amber-800 mb-1">Cancellation Policy</p>
        <ul className="text-amber-700 space-y-0.5">
          <li>• <strong>24+ hours before:</strong> Full refund</li>
          <li>• <strong>Less than 24 hours:</strong> No refund{formData.payment_type === 'deposit' && ', remaining balance due'}</li>
        </ul>
      </div>

      <div className="bg-stone-50 rounded-xl p-3 text-sm">
        <p className="font-medium text-stone-800">{formData.client_name}</p>
        <p className="text-stone-500 text-xs">{formData.client_email} • {formData.client_phone}</p>
      </div>

      <Button 
        onClick={handleSubmit} 
        disabled={loading || processingPayment || (promoApplied?.code === 'TREATHER' && !(formData.treat_recipient_name || '').trim())}
        className="w-full bg-luxury-green hover:bg-luxury-green/90 text-white rounded-full py-4 sm:py-5 text-sm sm:text-base"
        data-testid="booking-confirm-btn"
      >
        {processingPayment ? 'Redirecting to Payment...' : loading ? 'Processing...' : 
          voucherApplied && voucherApplied.remaining_balance >= finalPrice 
            ? 'Confirm Booking (Voucher)' 
            : voucherApplied 
              ? `Pay €${Math.max(0, finalPrice - voucherApplied.remaining_balance).toFixed(2)} Now`
              : `Pay €${payingNow.toFixed(2)} Now`
        }
      </Button>
      
      {!paymentConfig.payment_enabled && !voucherApplied && (
        <p className="text-center text-xs text-stone-400">
          Payment at the appointment. Confirmation email will be sent.
        </p>
      )}
    </div>
  );
};

export default BookingStepCheckout;
